import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { backupsApi } from '../api/client'

export function useBackups(instanceId: string, enabled = true) {
  return useQuery({
    queryKey: ['backups', instanceId],
    queryFn: () => backupsApi.list(instanceId),
    enabled: !!instanceId && enabled,
  })
}

export function useCreateBackup(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => backupsApi.create(instanceId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backups', instanceId] }),
  })
}

export function useUploadBackup(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => backupsApi.upload(instanceId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backups', instanceId] }),
  })
}

export function useDeleteBackup(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (backupId: string) => backupsApi.delete(instanceId, backupId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backups', instanceId] }),
  })
}

export function useRestoreBackup(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ backupId, neutralize }: { backupId: string; neutralize?: boolean }) =>
      backupsApi.restore(instanceId, backupId, neutralize ?? false),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances', instanceId] }),
  })
}

export function useReplaceFilestore(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => backupsApi.replaceFilestore(instanceId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances', instanceId] }),
  })
}

export function useImportOdooBackup(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ file, neutralize }: { file: File; neutralize?: boolean }) =>
      backupsApi.importOdooZip(instanceId, file, neutralize ?? false),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances', instanceId] }),
  })
}

export function useDownloadBackup(instanceId: string) {
  return useMutation({
    mutationFn: async ({ backupId, filename, part = 'db' }: { backupId: string; filename: string; part?: 'db' | 'filestore' }) => {
      const blob = await backupsApi.download(instanceId, backupId, part)
      const url = window.URL.createObjectURL(new Blob([blob]))
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
    },
  })
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1)
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}
